import React, { useEffect, useRef, useState } from "react";
import type { Player } from "../../../shared/types";

import { STEERING_LEFT, STEERING_RIGHT, STEERING_STRAIGHT, wormColors } from "core/src/core/constants.js";
import TouchFingerprint from "./TouchFingerprint";
import { useArenaLive } from "../../../game-canvas/arena-view";

/**
 * Renders 4 invisible touch zones over the full screen for two players sitting on
 * opposite sides of a phone laid flat between them. Layout (looking at the phone
 * from P1's side, the default screen orientation):
 *
 *   ┌───────────────┬───────────────┐
 *   │   P2 RIGHT    │   P2 LEFT     │   (P2 sits on the opposite side, L/R mirrored)
 *   ├───────────────┼───────────────┤
 *   │   P1 LEFT     │   P1 RIGHT    │   (P1 sits on the near side)
 *   └───────────────┴───────────────┘
 *
 * With ONE player the top half is theirs too: a touch anywhere that is not a
 * control steers, left or right by the side of the screen (see
 * onDocumentTouchStart; no zone covers the top, so its buttons keep working).
 *
 * Players passed in must include up to 2 humans; only the first two human players
 * receive touch zones. Bot players (type !== "human") are ignored here.
 */
type ZoneKey = "p1L" | "p1R" | "p2L" | "p2R";
type Pressed = Record<ZoneKey, boolean>;

interface Props {
  players: Player[];
  onSteeringUpdate: (playerId: any, steering: number) => void;
  // First-run hint (first-run.ts): through the start phase both of player 1's
  // prints pulse (until pressed), and in the left / right beats that zone is
  // lit below the board with a "Turn left" / "Turn right" label under its
  // print. One player only.
  hint?: "left" | "right" | "you" | null;
}

export default function TouchSteeringOverlay({ players, onSteeringUpdate, hint }: Props) {
  // A long map fills the screen, so the prints sit over the arena: they show
  // through the roll call and fade from the countdown on (the zones stay). A
  // first-run hint keeps its print up while it is showing.
  const live = useArenaLive();
  const [, setTick] = useState(0);
  // Refs are the synchronous source of truth so rapid multi-touch events don't
  // read stale state; the tick state just triggers re-render.
  const pressedRef = useRef<Pressed>({ p1L: false, p1R: false, p2L: false, p2R: false });
  const touchesRef = useRef<Record<string, ZoneKey>>({});
  const propsRef = useRef({ players, onSteeringUpdate });
  propsRef.current = { players, onSteeringUpdate };

  // Measure the real game board so the fingerprints sit a fixed distance off its
  // top/bottom edge - robust across phones (a vh-based guess drifted because the
  // mobile toolbar makes 100vh differ from the board's actual centred position).
  const overlayRef = useRef<HTMLDivElement>(null);
  // Sideways (a phone in landscape), the board fills the height and the free
  // room is beside it, so the prints go there instead: measured too.
  const [measuredEdges, setBoardEdges] = useState<{ top: number; bottom: number; left: number; right: number; width: number; height: number } | null>(null);
  useEffect(() => {
    if (typeof ResizeObserver === "undefined") return;
    let raf = 0;
    function measure() {
      const o = overlayRef.current;
      const board = document.querySelector("[class*='local-game-canvas-wrapper']");
      if (!o || !board) return;
      const ob = o.getBoundingClientRect();
      const bb = board.getBoundingClientRect();
      setBoardEdges({
        top: Math.round(bb.top - ob.top), bottom: Math.round(bb.bottom - ob.top),
        left: Math.round(bb.left - ob.left), right: Math.round(bb.right - ob.left),
        width: Math.round(ob.width), height: Math.round(ob.height),
      });
    }
    function schedule() {
      cancelAnimationFrame(raf);
      raf = requestAnimationFrame(measure);
    }
    schedule();
    const ro = new ResizeObserver(schedule);
    if (overlayRef.current) ro.observe(overlayRef.current);
    const board = document.querySelector("[class*='local-game-canvas-wrapper']");
    if (board) ro.observe(board);
    window.addEventListener("resize", schedule);
    return () => {
      ro.disconnect();
      window.removeEventListener("resize", schedule);
      cancelAnimationFrame(raf);
    };
  }, []);

  function applyZones(pressed: Pressed, touches: Record<string, ZoneKey>) {
    pressedRef.current = pressed;
    touchesRef.current = touches;
    setTick((t) => t + 1);

    const humanPlayers = propsRef.current.players.filter((p: any) => p.type === "human");
    const p1 = humanPlayers[0];
    const p2 = humanPlayers[1];
    if (p1) {
      let p1Steering = STEERING_STRAIGHT;
      if (pressed.p1L) p1Steering += STEERING_LEFT;
      if (pressed.p1R) p1Steering += STEERING_RIGHT;
      propsRef.current.onSteeringUpdate(p1.id, p1Steering);
    }
    if (p2) {
      let p2Steering = STEERING_STRAIGHT;
      if (pressed.p2L) p2Steering += STEERING_LEFT;
      if (pressed.p2R) p2Steering += STEERING_RIGHT;
      propsRef.current.onSteeringUpdate(p2.id, p2Steering);
    }
  }

  function handleTouchEnd(event: any) {
    const touches = { ...touchesRef.current };
    const pressed = { ...pressedRef.current };
    for (let i = 0; i < event.changedTouches.length; i++) {
      const id = event.changedTouches[i].identifier;
      const zoneKey = touches[id];
      if (zoneKey) {
        delete touches[id];
        // Only release the zone if no other active touch still holds it.
        const stillHeld = Object.keys(touches).some((k) => touches[k] === zoneKey);
        if (!stillHeld) {
          pressed[zoneKey] = false;
        }
      }
    }
    applyZones(pressed, touches);
  }

  function onTouchStart(event: React.TouchEvent) {
    event.preventDefault();
    const zoneKey = event.currentTarget.getAttribute("data-zone") as ZoneKey;
    const touches = { ...touchesRef.current };
    for (let i = 0; i < event.changedTouches.length; i++) {
      touches[event.changedTouches[i].identifier] = zoneKey;
    }
    const pressed = { ...pressedRef.current };
    pressed[zoneKey] = true;
    applyZones(pressed, touches);
  }

  function onTouchEnd(event: React.TouchEvent) {
    event.preventDefault();
    handleTouchEnd(event);
  }

  function onMouseDown(event: React.MouseEvent) {
    // Allow desktop testing with a mouse.
    event.preventDefault();
    const zoneKey = event.currentTarget.getAttribute("data-zone") as ZoneKey;
    const pressed = { ...pressedRef.current };
    pressed[zoneKey] = true;
    applyZones(pressed, touchesRef.current);
  }

  function onMouseUp(event: React.MouseEvent) {
    const zoneKey = event.currentTarget.getAttribute("data-zone") as ZoneKey;
    const pressed = { ...pressedRef.current };
    pressed[zoneKey] = false;
    applyZones(pressed, touchesRef.current);
  }

  function onContextMenu(event: React.MouseEvent) {
    // Prevent the long-press context menu on Android (can trigger haptic feedback).
    event.preventDefault();
  }

  useEffect(() => {
    // Some browsers fire touchend on document instead of the original target if the
    // finger moves outside the zone. Listen at document level too.
    function onDocumentTouchEnd(event: TouchEvent) {
      handleTouchEnd(event);
    }
    // One player on the phone: the whole screen steers, not only the bottom
    // half (the top half is the second player's when there are two). No zone is
    // laid over the top, so everything up there that is a control keeps working:
    // a touch that lands on a button, a link or a field, or while a dialog is
    // open, is left alone. Anything else steers, by the side of the screen.
    function onDocumentTouchStart(event: TouchEvent) {
      if (propsRef.current.players.filter((p: any) => p.type === "human").length !== 1) return;
      const o = overlayRef.current;
      const target = event.target as Element | null;
      if (!o || !target || !target.closest) return;
      if (target.closest("[data-zone]")) return; // the zones take their own
      if (target.closest("button, a, input, select, textarea, label, summary, [role='button'], [role='dialog'], [aria-modal='true'], [contenteditable='true'], [data-no-steer]")) return;
      if (document.querySelector("[role='dialog'], [aria-modal='true']")) return;
      // (and anything else made to be pressed: it shows a pointer)
      for (let el: Element | null = target, n = 0; el && n < 5; el = el.parentElement, n++) {
        if (window.getComputedStyle(el).cursor === "pointer") return;
      }
      const r = o.getBoundingClientRect();
      const touches = { ...touchesRef.current };
      const pressed = { ...pressedRef.current };
      for (let i = 0; i < event.changedTouches.length; i++) {
        const t = event.changedTouches[i];
        const zoneKey: ZoneKey = t.clientX < r.left + r.width / 2 ? "p1L" : "p1R";
        touches[t.identifier] = zoneKey;
        pressed[zoneKey] = true;
      }
      if (event.cancelable) event.preventDefault();
      applyZones(pressed, touches);
    }
    document.addEventListener("touchstart", onDocumentTouchStart, { passive: false });
    document.addEventListener("touchend", onDocumentTouchEnd);
    document.addEventListener("touchcancel", onDocumentTouchEnd);
    return () => {
      document.removeEventListener("touchstart", onDocumentTouchStart);
      document.removeEventListener("touchend", onDocumentTouchEnd);
      document.removeEventListener("touchcancel", onDocumentTouchEnd);
      // Reset everyone to straight on unmount.
      propsRef.current.players
        .filter((p: any) => p.type === "human")
        .forEach((p: any) => propsRef.current.onSteeringUpdate(p.id, STEERING_STRAIGHT));
    };
  }, []);

  const humanPlayers = players.filter((p: any) => p.type === "human");
  const p1 = humanPlayers[0];
  const p2 = humanPlayers[1];

  const zoneStyle: React.CSSProperties = {
    position: "absolute",
    width: "50%",
    height: "50%",
    background: "transparent",
    touchAction: "none",
    userSelect: "none",
    WebkitUserSelect: "none",
    WebkitTouchCallout: "none",
    WebkitTapHighlightColor: "transparent",
  } as React.CSSProperties;

  // Invisible full-quadrant touch target (the whole 50% x 50% zone steers).
  function renderZone(zoneKey: ZoneKey, positionStyle: React.CSSProperties) {
    const style: React.CSSProperties = { ...zoneStyle, ...positionStyle, pointerEvents: "auto" };
    return (
      <div
        key={zoneKey}
        data-zone={zoneKey}
        style={style}
        onTouchStart={onTouchStart}
        onTouchEnd={onTouchEnd}
        onTouchCancel={onTouchEnd}
        onMouseDown={onMouseDown}
        onMouseUp={onMouseUp}
        onMouseLeave={onMouseUp}
        onContextMenu={onContextMenu}
      />
    );
  }

  // A fingerprint hint, centred on the given screen-relative point (positioned
  // separately from the touch zones so it can hug the board edge, pulled inward).
  function renderPrint(zoneKey: ZoneKey, color: string, flip: boolean, left: string, top: string) {
    const hinted = !!hint && !p2 && (zoneKey === "p1L" || zoneKey === "p1R") && !pressedRef.current[zoneKey];
    return (
      <div
        key={"fp-" + zoneKey}
        className={hinted ? "touch-print-hinted" : undefined}
        style={{ position: "absolute", left, top, transform: "translate(-50%, -50%)", pointerEvents: "none", opacity: live && !hinted ? 0 : 1, transition: "opacity 0.35s ease" }}
      >
        <TouchFingerprint color={color} flip={flip} pressed={pressedRef.current[zoneKey]} />
      </div>
    );
  }

  // The first-run zone hints: each steering zone's free part (off the board:
  // below it upright, beside it sideways) tinted in the worm's colour during
  // its beat, fading in and out, and a label under each print saying what it
  // does, up from its beat until GO, then fading away. Both sides are kept
  // mounted through the start phase (and a beat past it) so they can fade.
  function renderZoneHint(side: "left" | "right", color: string, printLeft: string, printTop: string) {
    // the real board (not the square the prints are placed by)
    const b = measuredEdges;
    // (A board that fills the screen, or leaves a band too thin for a print,
    // has no free part to speak of: then the whole zone.)
    const band = b && b.left > 100;
    const box: React.CSSProperties = b && b.width > b.height
      ? { top: "50%", bottom: 0, width: band ? b.left : "50%", left: side === "left" ? 0 : band ? b.right : "50%" }
      : { top: b && b.height - b.bottom > 80 ? b.bottom : "50%", bottom: 0, width: "50%", left: side === "left" ? 0 : "50%" };
    // `shownHint` lags a frame behind `hint`, so a zone or label mounts at
    // opacity 0 first and then transitions in instead of popping.
    const h = shownHint;
    const zoneOn = h === side;
    const labelOn = side === "left" ? h === "left" || h === "right" || h === "you" : h === "right" || h === "you";
    return (
      <React.Fragment key={"hint-" + side}>
        <div className={"touch-zone-hint" + (zoneOn ? " is-on" : "")} style={{ ...box, color }} aria-hidden="true" />
        <div className={"touch-zone-hint-label" + (labelOn ? " is-on" : "")} style={{ left: printLeft, top: printTop, color }} role="status">
          {side === "left" ? "Turn left" : "Turn right"}
        </div>
      </React.Fragment>
    );
  }
  const [shownHint, setShownHint] = useState<typeof hint>(null);
  useEffect(() => {
    const id = requestAnimationFrame(() => setShownHint(hint));
    return () => cancelAnimationFrame(id);
  }, [hint]);
  // Mounted while a hint is up, and for the fade after the last one.
  const [hintFading, setHintFading] = useState(false);
  const hadHintRef = useRef(false);
  useEffect(() => {
    if (hint) {
      hadHintRef.current = true;
      setHintFading(false);
      return;
    }
    if (!hadHintRef.current) return;
    hadHintRef.current = false;
    setHintFading(true);
    const id = setTimeout(() => setHintFading(false), 500);
    return () => clearTimeout(id);
  }, [hint]);

  // A long map (the 2:1 rectangle or stadium) fills the screen, so its edges
  // would push the prints out to the screen's rim. They go where a square
  // board would put them instead (the largest square the screen holds,
  // centred): the same spots as on a square or a circle. They cover a bit of
  // the arena, but only until the countdown, when they fade.
  const boardEdges = measuredEdges && Math.abs((measuredEdges.right - measuredEdges.left) / Math.max(1, measuredEdges.bottom - measuredEdges.top) - 1) > 0.2
    ? (() => {
        const e = measuredEdges;
        const side = Math.min(e.width, e.height);
        return { ...e, top: (e.height - side) / 2, bottom: (e.height + side) / 2, left: (e.width - side) / 2, right: (e.width + side) / 2 };
      })()
    : measuredEdges;

  // Horizontal: inward of the phone edges. Vertical: a fixed gap off the measured
  // board top/bottom edge (so the distance to the board is consistent). Until the
  // board is measured, fall back to a sensible % so nothing flashes mis-placed.
  let IN = "26%";
  let OUT = "74%";
  const GAP = "30px"; // breathing room between the print and the board edge
  const HALF_PRINT = "clamp(27px, 7.5vmin, 52px)"; // ~half the fingerprint, to offset its centre
  // Kept on screen: a board that fills the screen (a long map) leaves no room
  // beyond its edge, so the prints come in over its ends instead.
  // (The bottom keeps room for the first-run "Turn left" label under a print.)
  const EDGE = "28px";
  const EDGE_BOTTOM = "64px";
  let NEAR_TOP = boardEdges ? `max(calc(${boardEdges.top}px - ${GAP} - ${HALF_PRINT}), calc(${EDGE} + 44px + ${HALF_PRINT}))` : "16%";
  let NEAR_BOTTOM = boardEdges ? `min(calc(${boardEdges.bottom}px + ${GAP} + ${HALF_PRINT}), calc(${boardEdges.height}px - ${EDGE_BOTTOM} - ${HALF_PRINT}))` : "84%";
  // Sideways: the board spans the height, so above/below it is off screen.
  // Player 1's prints sit in the bottom corners beside the board (centred in
  // the free band each side, or tucked over the board's corner when the band
  // is too narrow for a print), player 2's in the top corners, flipped. The
  // touch zones are still the screen's quadrants, so the prints stay inside
  // their own zones.
  const sideways = !!boardEdges && boardEdges.width > boardEdges.height;
  if (boardEdges && sideways) {
    const half = Math.min(52, Math.max(27, 0.075 * Math.min(boardEdges.width, boardEdges.height)));
    const band = boardEdges.left; // free room beside the board (same on both sides)
    const inset = band >= 2 * half + 8 ? band / 2 : 8 + half;
    IN = `${inset}px`;
    OUT = `${boardEdges.width - inset}px`;
    // Player 1's prints sit about two thirds of the way down the screen, where
    // thumbs rest when the phone is held by its ends, rather than at the very
    // bottom edge. Player 2's mirror that from the top, still clear of the
    // round panel and the toolbar in the top corners (about 44px).
    NEAR_TOP = `max(calc(${boardEdges.top}px + 48px + ${HALF_PRINT}), ${Math.round(boardEdges.height / 3)}px)`;
    NEAR_BOTTOM = `${Math.round((boardEdges.height * 2) / 3)}px`;
  }

  return (
    <div
      ref={overlayRef}
      className="touch-steering-overlay"
      style={{
        position: "absolute",
        top: 0,
        left: 0,
        width: "100%",
        height: "100%",
        zIndex: 600,
        pointerEvents: "none",
      }}
    >
      {p2 ? renderZone("p2R", { top: 0, left: 0 }) : null}
      {p2 ? renderZone("p2L", { top: 0, left: "50%" }) : null}
      {p1 ? renderZone("p1L", { top: "50%", left: 0 }) : null}
      {p1 ? renderZone("p1R", { top: "50%", left: "50%" }) : null}

      {p2 ? renderPrint("p2R", wormColors[p2.colorId], true, IN, NEAR_TOP) : null}
      {p2 ? renderPrint("p2L", wormColors[p2.colorId], true, OUT, NEAR_TOP) : null}
      {p1 ? renderPrint("p1L", wormColors[p1.colorId], false, IN, NEAR_BOTTOM) : null}
      {p1 ? renderPrint("p1R", wormColors[p1.colorId], false, OUT, NEAR_BOTTOM) : null}

      {(hint || hintFading) && p1 && !p2 ? [renderZoneHint("left", wormColors[p1.colorId], IN, NEAR_BOTTOM), renderZoneHint("right", wormColors[p1.colorId], OUT, NEAR_BOTTOM)] : null}
    </div>
  );
}
